import React, { useState, useEffect } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { PersonaPresetsData, DefaultConfig, DefaultAgentSettings } from '../AdminPersonasData';
import TemplateSettings from '../components/TemplateSettings';
import PersonaFields from '../components/PersonaFields';
import PromptPreview from '../components/PromptPreview';
import MasterTemplate from '../components/MasterTemplate';
import PersonaFormActions from '../components/PersonaFormActions';
import VoiceSelector from '../components/VoiceSelector';
import '../App.css';



const render_preview = (master_template, config) => {
	let result = master_template;
	Object.keys(config).forEach(function(key) {
		result = result.split('{{' + key + '}}').join(config[key] || '');
	});
	return result;
};

const load_admin_data = async (user_id, token, set_personas, set_master_template, set_loading, set_error, on_loaded) => {
	set_loading(true);
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};

		const [personas_res, template_res] = await Promise.all([
			axios.get(Constants.SERVER_URL + '/api/admin/personas', { headers }),
			axios.get(Constants.SERVER_URL + '/api/admin/master-template', { headers }),
		]);

		const loaded_personas = personas_res.data.personas || [];
		set_personas(loaded_personas);
		set_master_template(template_res.data.content || '');

		if(on_loaded) {
			on_loaded(loaded_personas);
		}
	} catch (error) {
		set_error('Failed to load admin data.');
	} finally {
		set_loading(false);
	}
};

const render_persona_list = (personas, selected_id, on_select, on_new) => {
	return (
		<div className="admin-persona-list">
			<div className="admin-persona-list-header">
				<span className="admin-section-label">Personas</span>
				<Button size="sm" variant="primary" onClick={on_new}>New</Button>
			</div>
			{personas.map(function(p) {
				const is_selected = p.id === selected_id;
				const class_name = 'admin-persona-item' + (is_selected ? ' admin-persona-item-selected' : '');
				return (
					<div key={p.id} className={class_name} onClick={function() { return on_select(p); }}>
						<span className="admin-persona-item-name">{p.name || p.prospect_name}</span>
						<span className="admin-persona-item-type">{p.business_type}</span>
					</div>
				);
			})}
		</div>
	);
};

function PersonaEditorView() {

	const { token, is_superadmin, auth_loading } = useAuth();

	const user_id = localStorage.getItem(Constants.USER_ID);

	const [personas, set_personas] = useState([]);
	const [selected_id, set_selected_id] = useState(null);
	const [config, set_config] = useState(DefaultConfig);
	const [agent_settings, set_agent_settings] = useState(DefaultAgentSettings);
	const [master_template, set_master_template] = useState('');
	const [voices, set_voices] = useState([]);
	const [loading, set_loading] = useState(false);
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');
	const [success, set_success] = useState('');

	useEffect(function() {
		if(user_id) {
			load_admin_data(user_id, token, set_personas, set_master_template, set_loading, set_error, function(loaded_personas) {
				if(loaded_personas.length > 0) {
					handle_select(loaded_personas[0]);
				}
			});
		}
	}, [token]);

	useEffect(function() {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		axios.get(Constants.SERVER_URL + '/api/call/rai_voices', { headers })
			.then(function(res) { set_voices(res.data.voices || []); })
			.catch(function() {});
	}, [token]);

	const handle_select = (persona) => {
		set_selected_id(persona.id);
		set_agent_settings({
			name: persona.prospect_name || persona.name || '',
			model: persona.model || 'gemini-2.5-flash-lite',
			temperature: persona.temperature || 0.7,
			agent_type: persona.agent_type || 'gemini',
			voice_id: persona.voice_id,
		});
		set_config({
			prospect_name: persona.prospect_name || '',
			business_name: persona.business_name || '',
			business_type: persona.business_type || 'Plumber',
			prospect_age: persona.prospect_age || 50,
			personality_trait: persona.personality_trait || '',
			scenario: persona.scenario || '',
			beliefs: persona.beliefs || '',
			initial_greeting: persona.initial_greeting || '',
			trade_question: persona.trade_question || '',
		});
		set_error('');
		set_success('');
	};

	const handle_new = () => {
		set_selected_id(null);
		set_config(DefaultConfig);
		set_agent_settings(DefaultAgentSettings);
		set_error('');
		set_success('');
	};

	const handle_config_change = (key, value) => {
		set_config(function(prev) {
			return Object.assign({}, prev, { [key]: value });
		});
		if(key === 'prospect_name') {
			set_agent_settings(function(prev) {
				return Object.assign({}, prev, { name: value });
			});
		}
	};

	const handle_agent_change = (key, value) => {
		set_agent_settings(function(prev) {
			return Object.assign({}, prev, { [key]: value });
		});
	};

	const handle_randomize = () => {
		const types = Object.keys(PersonaPresetsData);
		const type = types[Math.floor(Math.random() * types.length)];
		const values = PersonaPresetsData[type];

		set_config(function(prev) {
			return Object.assign({}, prev, values, { business_type: type });
		});

		set_agent_settings(function(prev) {
			return Object.assign({}, prev, { name: values.prospect_name || prev.name });
		});
	};

	const handle_save = async () => {
		const empty_field = Object.entries(config).find(function([, v]) { return !v && v !== 0; });
		if(empty_field) {
			set_error('All persona fields are required.');
			return;
		}
		set_saving(true);
		set_error('');
		set_success('');
		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			const body = { config, agent_settings };

			if(selected_id) {
				await axios.put(
					Constants.SERVER_URL + '/api/admin/personas/' + selected_id,
					body,
					{ headers }
				);
				set_success('Persona updated.');
			} else {
				const res = await axios.post(
					Constants.SERVER_URL + '/api/admin/personas',
					body,
					{ headers }
				);
				set_selected_id(res.data.id);
				set_success('Persona created.');
			}

			await load_admin_data(user_id, token, set_personas, set_master_template, function() {}, set_error);
		} catch (error) {
			set_error('Failed to save persona.');
		} finally {
			set_saving(false);
		}
	};

	const handle_delete = async () => {
		if(!selected_id) {
			return;
		}
		if(!window.confirm('Delete this persona? This cannot be undone.')) {
			return;
		}
		set_saving(true);
		set_error('');
		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			await axios.delete(
				Constants.SERVER_URL + '/api/admin/personas/' + selected_id,
				{ headers }
			);
			set_selected_id(null);
			set_config(DefaultConfig);
			set_agent_settings(DefaultAgentSettings);
			set_success('Persona deleted.');
			await load_admin_data(user_id, token, set_personas, set_master_template, function() {}, set_error);
		} catch (error) {
			set_error('Failed to delete persona.');
		} finally {
			set_saving(false);
		}
	};

	const handle_save_master_template = async () => {
		set_saving(true);
		set_error('');
		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			await axios.put(
				Constants.SERVER_URL + '/api/admin/master-template',
				{ content: master_template },
				{ headers }
			);
			set_success('Master template saved.');
		} catch (error) {
			set_error('Failed to save master template.');
		} finally {
			set_saving(false);
		}
	};

	const preview = render_preview(master_template, Object.assign({}, agent_settings, config));

	return (
		<main className="admin-main">
			<h4 className="admin-title">Prospect Personas</h4>
			<p className="sub-title-text">Create or a edit a custom personality to call</p>

			{error ? <Alert variant="danger">{error}</Alert> : null}
			{success ? <Alert variant="success">{success}</Alert> : null}

			{loading ? (
				<div className="admin-loading"><Spinner animation="border" /></div>
			) : (
				<div className="admin-persona-layout">

					{render_persona_list(personas, selected_id, handle_select, handle_new)}

					<div className="admin-form-panel">

						{is_superadmin && <TemplateSettings
							agent_settings={agent_settings}
							on_change={handle_agent_change}/>}

						<VoiceSelector
							voice_id={agent_settings.voice_id}
							voices={voices}
							on_change={handle_agent_change}
							token={token}
						/>

						<PersonaFields config={config} on_change={handle_config_change} on_randomize={handle_randomize}/>

						<PersonaFormActions
							on_save={handle_save}
							on_delete={handle_delete}
							saving={saving}
							selected_id={selected_id}
						/>

						{is_superadmin && <PromptPreview preview={preview} /> }

						{is_superadmin && <MasterTemplate
							master_template={master_template}
							on_change={set_master_template}
							on_save={handle_save_master_template}
							saving={saving} /> }
					</div>
				</div>
			)}
		</main>
	);
}

export default PersonaEditorView;
